/**
 * Device Data Service
 * Handles all API calls for device data with mock implementations
 * Ready for backend integration
 */

// Define the vibrant color palette (inspired by modern data visualization)
const CHART_COLORS = [
  '#5B9BD5', // Cornflower Blue
  '#70AD47', // Fresh Green
  '#FFC000', // Golden Yellow
  '#ED7D31', // Bright Orange
  '#E15759', // Coral Red
  '#9E7BB5', // Purple/Lavender
  '#4472C4', // Deep Blue
  '#A5A5A5', // Medium Grey
  '#264478', // Navy Blue
];

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Normalize throughput statistics returned by the gateway facade.
 * The backend returns one record per MQI test type; the portal consumes a
 * stable uplink/downlink shape so both aggregate views can share one panel.
 */
export function transformThroughputStats(apiData) {
  const stats = Array.isArray(apiData?.testTypeStats) ? apiData.testTypeStats : [];

  const toDirection = (testType) => {
    const normalized = String(testType || '').toUpperCase();
    if (normalized.includes('UPLINK') || normalized === 'UL') return 'uplink';
    if (normalized.includes('DOWNLINK') || normalized === 'DL') return 'downlink';
    return null;
  };

  const normalizeStats = (entry, direction) => ({
    direction,
    sampleCount: numberOrNull(entry?.sampleCount) || 0,
    minRateMbps: numberOrNull(entry?.minRateMbps),
    maxRateMbps: numberOrNull(entry?.maxRateMbps),
    avgRateMbps: numberOrNull(entry?.avgRateMbps),
    stdDevMbps: numberOrNull(entry?.stdDevMbps),
    p50RateMbps: numberOrNull(entry?.p50RateMbps),
    p95RateMbps: numberOrNull(entry?.p95RateMbps),
    p99RateMbps: numberOrNull(entry?.p99RateMbps),
    thresholdMbps: numberOrNull(entry?.thresholdMbps),
    qualifyingSampleCount: numberOrNull(entry?.qualifyingSampleCount),
    qualifyingSamplePercentage: numberOrNull(entry?.qualifyingSamplePercentage),
    trendData: Array.isArray(entry?.trendData)
      ? entry.trendData
        .map((point) => ({
          x: point?.bucketTime,
          y: numberOrNull(point?.avgRateMbps),
        }))
        .filter((point) => point.x && point.y !== null)
      : [],
    outliers: Array.isArray(entry?.outliers) ? entry.outliers : [],
  });

  const normalized = { uplink: null, downlink: null };
  stats.forEach((entry) => {
    const direction = toDirection(entry?.testType);
    if (direction) normalized[direction] = normalizeStats(entry, direction);
  });

  return {
    ...normalized,
    totalSamples: numberOrNull(apiData?.totalSamples) || 0,
    deviceCount: numberOrNull(apiData?.deviceCount),
    timeRange: apiData?.timeRange || null,
  };
}

class DeviceDataService {
  constructor() {
    this.cache = new Map();
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
    const wbcApiHost = (process.env.REACT_APP_WBC_API_BASE_URL || 'http://localhost:8080').replace(/\/$/, '');
    const queryServicePathRaw = process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1';
    const queryServicePath = queryServicePathRaw.startsWith('/') ? queryServicePathRaw : `/${queryServicePathRaw}`;
    this.wbcApiBaseUrl = `${wbcApiHost}${queryServicePath}`;
    this.wbcApiNetworkHealthBaseUrl = this.wbcApiBaseUrl;
    this.wbcApiDeviceHealthBaseUrl = this.wbcApiBaseUrl;
    this.wbcApiBootKpiBaseUrl = this.wbcApiBaseUrl;
    this.panelApiTimeout = parseInt(process.env.REACT_APP_PANEL_API_TIMEOUT) || 60000; // Unified timeout for all API calls
  }


  /**
   * Convert time range to epoch timestamps
   */
  timeRangeToEpoch(timeRange) {
    // Handle custom range objects with exact timestamps
    if (typeof timeRange === 'object' && timeRange.type === 'custom') {
      const fromDate = Math.floor(new Date(timeRange.from).getTime() / 1000);
      const toDate = Math.floor(new Date(timeRange.to).getTime() / 1000);
      
      
      return {
        fromDate,
        toDate
      };
    }

    // Handle preset string ranges
    const now = Math.floor(Date.now() / 1000); // Current time in epoch seconds
    const ranges = {
      '5m': now - 300,
      '15m': now - 900,
      '30m': now - 1800,
      '1h': now - 3600,
      '3h': now - 10800,
      '6h': now - 21600,
      '12h': now - 43200,
      '24h': now - 86400,
      '2d': now - 172800,
      '7d': now - 604800,
      '14d': now - 1209600,
      '30d': now - 2592000,
      '90d': now - 7776000
    };

    const fromDate = ranges[timeRange] || ranges['24h'];

    
    return {
      fromDate,
      toDate: now
    };
  }

  /**
   * Get device info charts from aggregate table via single API call.
   * Endpoint lives in wbc-api-gateway and reads the successor device-info projection.
   */
  async getDeviceInfoAggregate(deviceId, timeRange = '24h') {
    if (!deviceId) {
      throw new Error('System ID required');
    }

    const trimmedSystemId = deviceId.trim();
    const durationDays = this.timeRangeToDurationDays(timeRange);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      const url = `${this.wbcApiBaseUrl}/device-info?systemId=${encodeURIComponent(trimmedSystemId)}&durationDays=${durationDays}`;
      console.log('[API]', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      if (response.status === 204) {
        return {
          licenseSN: { labels: [], data: [], colors: [] },
          ratDistribution: { labels: [], data: [], colors: [] },
          flashMemoryPartitions: { labels: [], data: [], colors: [] }
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiData = await response.json();

      const licenseRows = apiData.licenseSn || apiData.license_sn_distribution || [];
      const ratRows = apiData.ratDistribution || apiData.rat_distribution || [];
      const flashRows = apiData.flashMemoryPartitions || apiData.flash_memory_partitions || [];

      const result = {
        freshness: {
          latestSnapshotDateUtc: apiData.latestSnapshotDateUtc || null,
          dataAgeDays: typeof apiData.dataAgeDays === 'number' ? apiData.dataAgeDays : null,
          dataUpdatedThroughUtc: apiData.dataUpdatedThroughUtc || null,
          dataUpdatedAtUtc: apiData.dataUpdatedAtUtc || null,
          rowsFound: typeof apiData.rowsFound === 'number' ? apiData.rowsFound : 0
        },
        metadata: this.transformDeviceInfoMetadata(apiData),
        licenseSN: this.transformLicenseDistribution(licenseRows),
        ratDistribution: this.transformRatDistribution(ratRows),
        flashMemoryPartitions: this.transformFlashPartitionDistribution(flashRows)
      };
      return result;
    } catch (error) {
      console.error('❌ Device Info Aggregate API Error:', error);

      if (error.name === 'AbortError') {
        const timeoutInSeconds = this.panelApiTimeout / 1000;
        throw new Error(`Request timeout after ${timeoutInSeconds} seconds - please try again`);
      }

      if (error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }

      throw error;
    }
  }

  /**
   * Get device health data from aggregate table via single API call.
   * Endpoint lives in wbc-api-gateway and reads the successor device-health projection.
   */
  async getDeviceHealthAggregate(deviceId, timeRange = '24h') {
    if (!deviceId) {
      throw new Error('System ID required');
    }

    const trimmedSystemId = deviceId.trim();
    const { startDate, endDate } = this.buildDeviceHealthDateWindow(timeRange);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      const url = `${this.wbcApiDeviceHealthBaseUrl}/device-health/deviceHealthData?systemId=${encodeURIComponent(trimmedSystemId)}&startDate=${startDate}&endDate=${endDate}`;
      console.log('[API]', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiData = await response.json();

      return {
        freshness: {
          latestSnapshotDateUtc: apiData.latestSnapshotDateUtc || null,
          dataAgeDays: typeof apiData.dataAgeDays === 'number' ? apiData.dataAgeDays : null,
          dataUpdatedThroughUtc: apiData.dataUpdatedThroughUtc || null,
          dataUpdatedAtUtc: apiData.dataUpdatedAtUtc || null,
          rowsFound: typeof apiData.rowsFound === 'number' ? apiData.rowsFound : 0
        },
        thermalData: { series: this.transformDeviceHealthThermal(apiData.thermal_trends || []) },
        flashUsage: { series: this.transformDeviceHealthFlash(apiData.flash_usage || []) },
        memoryUsage: { series: this.transformDeviceHealthMemory(apiData.memory_usage || []) },
        cpuUsage: { series: this.transformDeviceHealthCpu(apiData.cpu_usage || []) },
        subsystemRestartEvents: this.transformDeviceHealthSubsystemRestartEvents(apiData.subsystem_restart || []),
        subsystemRestartTimeline: this.transformDeviceHealthSubsystemRestartTimeline(apiData.subsystem_restart || [])
      };
    } catch (error) {
      console.error('❌ Device Health Aggregate API Error:', error);

      if (error.name === 'AbortError') {
        const timeoutInSeconds = this.panelApiTimeout / 1000;
        throw new Error(`Request timeout after ${timeoutInSeconds} seconds - please try again`);
      }

      if (error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }

      throw error;
    }
  }

  /**
   * Get network health data from aggregate table via single API call.
   * Endpoint lives in wbc-api-gateway and reads the successor network-health projection.
   */
  async getNetworkHealthAggregate(deviceId, timeRange = '24h') {
    if (!deviceId) {
      throw new Error('System ID required');
    }

    const trimmedSystemId = deviceId.trim();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      const queryParams = this.buildNetworkHealthQueryParams(timeRange);
      const url = `${this.wbcApiNetworkHealthBaseUrl}/network-health/device/${encodeURIComponent(trimmedSystemId)}?${queryParams}`;
      console.log('[API]', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiData = await response.json();

      const registration = apiData.registration || {};
      const pdn = apiData.pdn || {};
      const dataStall = apiData.dataStall || {};

      const networkHealthData = {
        freshness: {
          latestSnapshotDateUtc: apiData.latestSnapshotDateUtc || null,
          dataAgeDays: typeof apiData.dataAgeDays === 'number' ? apiData.dataAgeDays : null,
          pipelineCoverageEndUtc: apiData.pipelineCoverageEndUtc || null,
          pipelineLastRunFinishedAtUtc: apiData.pipelineLastRunFinishedAtUtc || null,
          queryStartDateUtc: apiData.startDateUtc || null,
          queryEndDateUtc: apiData.endDateUtc || null,
          rowsFound: typeof apiData.rowsFound === 'number' ? apiData.rowsFound : 0
        },
        registrationStatus: this.transformRegistrationStatus(registration.statusDistribution),
        registrationFailureReason: this.transformRegistrationFailureReason(registration.failureReasons),
        registrationStatusEvents: this.transformRegistrationStatusEvents(registration),

        pdnStatus: this.transformPdnStatus(pdn.statusDistribution),
        pdnStatusDownReason: this.transformPdnFailureReason(pdn.failureReasons),
        pdnApnTypeDistribution: this.transformPdnApnTypeDistribution(pdn.apnTypeDistribution),
        pdnStatusEvents: this.transformPdnStatusEvents(pdn),

        dataStallSubsystem: this.transformDataStallSubsystem(dataStall.subsystemDistribution),
        dataStallRecoveryActions: this.transformDataStallRecoveryActions(dataStall.recoveryActionsDistribution),
        dataStallTimeline: this.transformDataStallTimeline(dataStall.timeline),

        peakRate: this.transformPeakRateData(apiData.peakRate || null),

        ...this.transformSignalHistograms(apiData.signalHistograms || null),

        anomalousSignalValues: apiData.anomalousSignalTimeSeries || null,
        ...this.transformAnomalousSignalStats(apiData.anomalousSignalStats || null)
      };

      return networkHealthData;
    } catch (error) {
      console.error('❌ Network Health Aggregate API Error:', error);

      if (error.name === 'AbortError') {
        const timeoutInSeconds = this.panelApiTimeout / 1000;
        throw new Error(`Request timeout after ${timeoutInSeconds} seconds - please try again`);
      }

      if (error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }

      throw error;
    }
  }

  /**
   * Get boot KPI data from aggregate table via single API call.
   * Endpoint lives in wbc-api-gateway and reads the successor boot-KPI projection.
   */
  async getBootKpiAggregate(deviceId, timeRange = '24h') {
    if (!deviceId) {
      throw new Error('System ID required');
    }

    const trimmedSystemId = deviceId.trim();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      const queryParams = this.buildNetworkHealthQueryParams(timeRange);
      const url = `${this.wbcApiBootKpiBaseUrl}/boot-kpi/device/${encodeURIComponent(trimmedSystemId)}?${queryParams}`;
      console.log('[API]', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiData = await response.json();
      return {
        freshness: {
          latestSnapshotDateUtc: apiData.latestSnapshotDateUtc || null,
          dataAgeDays: typeof apiData.dataAgeDays === 'number' ? apiData.dataAgeDays : null,
          pipelineCoverageEndUtc: apiData.pipelineCoverageEndUtc || null,
          pipelineLastRunFinishedAtUtc: apiData.pipelineLastRunFinishedAtUtc || null,
          queryStartDateUtc: apiData.startDateUtc || null,
          queryEndDateUtc: apiData.endDateUtc || null,
          rowsFound: typeof apiData.rowsFound === 'number' ? apiData.rowsFound : 0
        },
        kpiDistribution: this.transformAggregateKpiDistribution(apiData.kpiDistribution || []),
        timingHistograms: this.transformAggregateTimingHistograms(apiData.timingHistograms || {})
      };
    } catch (error) {
      console.error('❌ Boot KPI Aggregate API Error:', error);

      if (error.name === 'AbortError') {
        const timeoutInSeconds = this.panelApiTimeout / 1000;
        throw new Error(`Request timeout after ${timeoutInSeconds} seconds - please try again`);
      }

      if (error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }

      throw error;
    }
  }

  /**
   * Build query parameters for health endpoints
   * Uses 'range' parameter for preset ranges (gets backend cache) or fromDate/toDate for custom ranges
   */
  buildHealthQueryParams(deviceId, timeRange) {
    const trimmedDeviceId = encodeURIComponent(deviceId.trim());
    const backendRange = this.mapTimeRangeToBackendRange(timeRange);

    if (backendRange) {
      // Use range parameter for cached responses
      return `deviceId=${trimmedDeviceId}&range=${backendRange}`;
    } else {
      // Use fromDate/toDate for custom ranges (bypasses cache for fresh data)
      const { fromDate, toDate } = this.timeRangeToEpoch(timeRange);
      return `deviceId=${trimmedDeviceId}&fromDate=${fromDate}&toDate=${toDate}`;
    }
  }

  /**
   * Transform Device Health API response to chart format
   */
  transformDeviceHealthData(apiData, timeRange, subsystemRestartEvents = null, subsystemRestartTimeline = null) {
    if (!apiData) {
      return {
        thermalTrends: { series: [] },
        flashUsage: { series: [] },
        memoryUsage: { series: [] },
        cpuUsage: { series: [] },
        subsystemRestartEvents: subsystemRestartEvents || { labels: [], data: [], colors: [] },
        subsystemRestartTimeline: subsystemRestartTimeline || { series: [] }
      };
    }


    // Transform thermal data
    const thermalSeries = this.transformThermalData(apiData.thermalData || []);

    // Transform flash usage data
    const flashSeries = this.transformFlashHealthData(apiData.flashUsage || []);

    // Transform memory usage data
    const memorySeries = this.transformMemoryData(apiData.memoryUsage || []);

    // Transform CPU usage data
    const cpuSeries = this.transformCpuData(apiData.cpuUsage || []);

    const result = {
      thermalTrends: { series: thermalSeries },
      flashUsage: { series: flashSeries },
      memoryUsage: { series: memorySeries },
      cpuUsage: { series: cpuSeries },
      subsystemRestartEvents: subsystemRestartEvents || { labels: [], data: [], colors: [] },
      subsystemRestartTimeline: subsystemRestartTimeline || { series: [] }
    };

    return result;
  }

  /**
   * Transform thermal data from API to time series format
   */
  transformThermalData(thermalData) {
    if (!Array.isArray(thermalData)) return [];

    // Group thermal data by identifier
    const thermalByIdentifier = {};
    thermalData.forEach(point => {
      const identifier = point.identifier || 'Unknown';
      if (!thermalByIdentifier[identifier]) {
        thermalByIdentifier[identifier] = [];
      }
      // Only add data points that have real timestamps from API
      if (point.timestamp) {
        thermalByIdentifier[identifier].push({
          x: point.timestamp,
          y: point.temperatureCelsius || 0
        });
      }
    });

    // Convert to series format with colors
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#95a5a6'];
    let colorIndex = 0;

    return Object.keys(thermalByIdentifier).map((identifier, index) => ({
      name: identifier,
      data: thermalByIdentifier[identifier].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: colors[colorIndex++ % colors.length],
      hidden: index > 0 // Show first series, hide others by default
    }));
  }

  /**
   * Transform flash usage data from API to time series format
   */
  transformFlashHealthData(flashData) {
    if (!Array.isArray(flashData)) return [];

    // Group flash data by identifier
    const flashByIdentifier = {};
    flashData.forEach(point => {
      const identifier = point.identifier || 'Unknown';
      if (!flashByIdentifier[identifier]) {
        flashByIdentifier[identifier] = [];
      }
      // Only add data points that have real timestamps from API
      if (point.timestamp) {
        flashByIdentifier[identifier].push({
          x: point.timestamp,
          y: point.usedMemoryMb || 0
        });
      }
    });

    // Convert to series format with colors
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#95a5a6'];
    let colorIndex = 0;

    return Object.keys(flashByIdentifier).map((identifier, index) => ({
      name: identifier,
      data: flashByIdentifier[identifier].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: colors[colorIndex++ % colors.length],
      hidden: index > 0 // Show first series, hide others by default
    }));
  }

  /**
   * Transform memory usage data from API to time series format
   */
  transformMemoryData(memoryData) {
    if (!Array.isArray(memoryData)) return [];

    // Group memory data by identifier
    const memoryByIdentifier = {};
    memoryData.forEach(point => {
      const identifier = point.identifier || 'Unknown';
      if (!memoryByIdentifier[identifier]) {
        memoryByIdentifier[identifier] = [];
      }
      // Only add data points that have real timestamps from API
      if (point.timestamp) {
        memoryByIdentifier[identifier].push({
          x: point.timestamp,
          y: point.usedMemoryMb || 0
        });
      }
    });

    // Convert to series format with colors
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6'];
    let colorIndex = 0;

    return Object.keys(memoryByIdentifier).map((identifier, index) => ({
      name: identifier,
      data: memoryByIdentifier[identifier].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: colors[colorIndex++ % colors.length],
      hidden: index > 1 // Show first two series, hide others by default
    }));
  }

  /**
   * Transform CPU usage data from API to time series format
   */
  transformCpuData(cpuData) {
    if (!Array.isArray(cpuData)) return [];

    // Group CPU data by identifier
    const cpuByIdentifier = {};
    cpuData.forEach(point => {
      const identifier = point.identifier || 'Unknown';
      if (!cpuByIdentifier[identifier]) {
        cpuByIdentifier[identifier] = [];
      }
      // Only add data points that have real timestamps from API
      if (point.timestamp) {
        cpuByIdentifier[identifier].push({
          x: point.timestamp,
          y: Math.min(100, Math.max(0, point.userPercent || 0)) // Clamp CPU percentage to 0-100
        });
      }
    });

    // Convert to series format with colors
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6'];
    let colorIndex = 0;

    return Object.keys(cpuByIdentifier).map((identifier, index) => ({
      name: identifier,
      data: cpuByIdentifier[identifier].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: colors[colorIndex++ % colors.length],
      hidden: index > 0 // Show first series, hide others by default
    }));
  }

  /**
   * Transform Boot KPI API response to chart format
   */
  transformBootKpiData(apiData) {
    if (!apiData) {
      return {
        kpiDistribution: { labels: [], data: [], colors: [] },
        timingHistograms: {}
      };
    }


    // Transform KPI distribution for pie chart
    const kpiDistribution = this.transformKpiDistribution(apiData.kpiDistribution || []);
    
    // Transform timing histograms
    const timingHistograms = this.transformTimingHistograms(apiData.timingHistograms || {});

    const result = {
      kpiDistribution,
      timingHistograms
    };

    return result;
  }

  /**
   * Transform KPI Distribution API response to pie chart format
   */
  transformKpiDistribution(kpiDistribution) {
    if (!Array.isArray(kpiDistribution) || kpiDistribution.length === 0) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#9b59b6', '#1abc9c', '#e67e22', '#95a5a6', '#34495e'];
    
    return {
      labels: kpiDistribution.map(item => item.kpiType || 'Unknown'),
      data: kpiDistribution.map(item => item.count || 0),
      colors: kpiDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Timing Histograms to statistical summaries
   */
  transformTimingHistograms(timingHistograms) {
    if (!timingHistograms || typeof timingHistograms !== 'object') {
      return {};
    }

    const transformedStats = {};
    
    Object.entries(timingHistograms).forEach(([kpiType, timings]) => {
      if (Array.isArray(timings) && timings.length > 0) {
        transformedStats[kpiType] = this.calculateTimingStatistics(timings);
      } else {
        transformedStats[kpiType] = {
          count: 0,
          mean: 0,
          min: 0,
          max: 0,
          stdDev: 0
        };
      }
    });

    return transformedStats;
  }

  /**
   * Calculate statistical summary from timing data
   */
  calculateTimingStatistics(timings) {
    if (!Array.isArray(timings) || timings.length === 0) {
      return {
        count: 0,
        mean: 0,
        min: 0,
        max: 0,
        stdDev: 0
      };
    }

    const count = timings.length;
    const min = Math.min(...timings);
    const max = Math.max(...timings);
    const mean = timings.reduce((sum, value) => sum + value, 0) / count;
    
    // Calculate standard deviation
    const variance = timings.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / count;
    const stdDev = Math.sqrt(variance);

    return {
      count,
      mean,
      min,
      max,
      stdDev
    };
  }

  /**
   * Transform License Distribution API response to pie chart format
   */
  transformLicenseDistribution(apiData) {
    // Use the vibrant color palette
    const colors = CHART_COLORS;
    
    return {
      labels: apiData.map(item => item.licenseSn || 'Unknown'),
      data: apiData.map(item => item.count || 0),
      colors: apiData.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform RAT Distribution API response to pie chart format
   */
  transformRatDistribution(apiData) {
    // Use the vibrant color palette
    const colors = CHART_COLORS;
    
    return {
      labels: apiData.map(item => item.wwanTech || 'Unknown'),
      data: apiData.map(item => item.count ?? item.uploadCount ?? 0),
      colors: apiData.map((_, index) => colors[index % colors.length])
    };
  }

  transformDeviceInfoMetadata(apiData) {
    const md = apiData?.metadata || {};
    return {
      device_id: apiData?.systemId || '',
      device_info_device_make: md.deviceInfoDeviceMake || 'Unknown',
      device_info_device_model: md.deviceInfoDeviceModel || 'Unknown',
      nw_info_operator_sub1: md.nwInfoOperatorSub1 || 'Unknown',
      device_info_oem: md.deviceInfoOem || 'Unknown',
      device_info_hw_type: md.deviceInfoHwType || 'Unknown',
      device_info_chipset_id: md.deviceInfoChipsetId || 'Unknown',
      apss_info_build_id: md.apssInfoBuildId || 'Unknown',
      mpss_info_build_id: md.mpssInfoBuildId || 'Unknown'
    };
  }

  /**
   * Transform Flash Partition Distribution API response to pie chart format
   * Used for the Device Information section pie chart
   */
  transformFlashPartitionDistribution(apiData) {
    // Use the vibrant color palette
    const colors = CHART_COLORS;
    
    return {
      labels: apiData.map(item => item.partitionName || 'Unknown'),
      data: apiData.map(item => item.totalMemoryMb || 0),
      colors: apiData.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Flash Usage API response to pie chart format (DEPRECATED - kept for backward compatibility)
   * This method is no longer used but kept in case old code references it
   */
  transformFlashUsage(partitionData) {
    const colors = CHART_COLORS; // Use the vibrant color palette
    
    return {
      labels: partitionData.map(item => item.partitionName || 'Unknown'),
      data: partitionData.map(item => item.totalMemoryMb || 0),
      colors: partitionData.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Registration Status API response to pie chart format
   */
  transformRegistrationStatus(statusDistribution) {
    if (!statusDistribution || !Array.isArray(statusDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = [CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[0]]; // Green, Red, Orange, Blue for status
    
    return {
      labels: statusDistribution.map(item => item.registrationStatus || 'Unknown'),
      data: statusDistribution.map(item => item.count || 0),
      colors: statusDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Registration Failure Reason API response to pie chart format
   */
  transformRegistrationFailureReason(failureReasons) {
    if (!failureReasons || !Array.isArray(failureReasons)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors, prioritizing reds/oranges for failure reasons
    const colors = [CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[2], CHART_COLORS[6]]; // Red, Orange, Yellow, Deep Blue
    
    return {
      labels: failureReasons.map(item => item.rejectCause || 'Unknown'),
      data: failureReasons.map(item => item.count || 0),
      colors: failureReasons.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform PDN Status API response to pie chart format
   */
  transformPdnStatus(statusDistribution) {
    if (!statusDistribution || !Array.isArray(statusDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors for PDN status
    const colors = CHART_COLORS;
    
    return {
      labels: statusDistribution.map(item => item.pdnStatus || 'Unknown'),
      data: statusDistribution.map(item => item.count || 0),
      colors: statusDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform PDN Failure Reason API response to pie chart format
   */
  transformPdnFailureReason(failureReasons) {
    if (!failureReasons || !Array.isArray(failureReasons)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors for PDN failure reasons
    const colors = CHART_COLORS;

    return {
      labels: failureReasons.map(item => item.failureReason || 'Unknown'),
      data: failureReasons.map(item => item.count || 0),
      colors: failureReasons.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform PDN APN Type distribution to pie chart format
   */
  transformPdnApnTypeDistribution(apnTypeDistribution) {
    if (!apnTypeDistribution || !Array.isArray(apnTypeDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = CHART_COLORS;

    return {
      labels: apnTypeDistribution.map(item => item.apnType || 'Unknown'),
      data: apnTypeDistribution.map(item => item.count || 0),
      colors: apnTypeDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform PDN Events API response to event time series format
   */
  transformPdnStatusEvents(apiData) {
    if (!apiData || !apiData.statusTimeSeries || !Array.isArray(apiData.statusTimeSeries)) {
      return { eventSeries: [] };
    }


    // Group events by PDN status
    const eventsByStatus = {};
    apiData.statusTimeSeries.forEach(event => {
      const status = event.pdnStatus || 'Unknown';
      if (!eventsByStatus[status]) {
        eventsByStatus[status] = [];
      }
      // Only add events that have real timestamps from API
      if (event.timestamp) {
        eventsByStatus[status].push({
          x: event.timestamp
        });
      }
    });

    // Convert to event series format with colors
    const statusColors = {
      'PDN_IPV6_UP': '#3498db',
      'PDN_IPV4_UP': '#2ecc71',
      'PDN_IPV4_DOWN': '#e74c3c',
      'PDN_IPV6_DOWN': '#9b59b6',
      'PDN_STATUS_IPV6_UP': '#3498db',
      'PDN_STATUS_IPV4_UP': '#2ecc71',
      'PDN_STATUS_IPV4_DOWN': '#e74c3c',
      'PDN_STATUS_IPV6_DOWN': '#9b59b6'
    };

    const eventSeries = Object.keys(eventsByStatus).map(status => ({
      name: status,
      events: eventsByStatus[status].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: statusColors[status] || '#95a5a6'
    }));

    return { eventSeries };
  }

  /**
   * Transform Registration Events API response to event time series format
   */
  transformRegistrationStatusEvents(apiData) {
    if (!apiData || !apiData.statusTimeSeries || !Array.isArray(apiData.statusTimeSeries)) {
      return { eventSeries: [] };
    }


    // Group events by registration status
    const eventsByStatus = {};
    apiData.statusTimeSeries.forEach(event => {
      const status = event.registrationStatus || 'Unknown';
      if (!eventsByStatus[status]) {
        eventsByStatus[status] = [];
      }
      // Only add events that have real timestamps from API
      if (event.timestamp) {
        eventsByStatus[status].push({
          x: event.timestamp
        });
      }
    });

    // Convert to event series format with colors
    const statusColors = {
      'REGISTRATION_UP': '#2ecc71',
      'REGISTRATION_DOWN': '#e74c3c',
      'REGISTRATION_STATUS_UP': '#2ecc71',
      'REGISTRATION_STATUS_DOWN': '#e74c3c'
    };

    const eventSeries = Object.keys(eventsByStatus).map(status => ({
      name: status,
      events: eventsByStatus[status].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: statusColors[status] || '#95a5a6'
    }));

    return { eventSeries };
  }

  /**
   * Transform Peak Rate API response to time series format
   */
  transformPeakRateData(apiData) {
    if (!apiData) {
      return {
        series: [
          { name: 'Uplink', data: [], color: '#e74c3c' },
          { name: 'Downlink', data: [], color: '#2ecc71' }
        ]
      };
    }


    // Transform uplink data - only include points with real timestamps
    const uplinkData = (apiData.uplinkTimeSeries || [])
      .filter(point => point.timestamp)
      .map(point => ({
        x: point.timestamp,
        y: point.peakRateMbps || 0
      }))
      .sort((a, b) => new Date(a.x) - new Date(b.x));

    // Transform downlink data - only include points with real timestamps
    const downlinkData = (apiData.downlinkTimeSeries || [])
      .filter(point => point.timestamp)
      .map(point => ({
        x: point.timestamp,
        y: point.peakRateMbps || 0
      }))
      .sort((a, b) => new Date(a.x) - new Date(b.x));

    const result = {
      series: [
        {
          name: 'Uplink',
          data: uplinkData,
          color: '#e74c3c'
        },
        {
          name: 'Downlink',
          data: downlinkData,
          color: '#2ecc71'
        }
      ]
    };

    return result;
  }

  /**
   * Transform Signal Histograms API response to histogram format
   */
  transformSignalHistograms(apiData) {
    if (!apiData) {
      return {
        rsrpHistogram: { bins: [] },
        rsrqHistogram: { bins: [] },
        sinrHistogram: { bins: [] }
      };
    }


    // Helper function to fix the last bin range to use ] instead of )
    const fixHistogramRanges = (bins) => {
      return bins.map((bin, index, array) => {
        let range = bin.binInterval || 'Unknown';
        
        // If this is the last bin and the range ends with ), change it to ]
        if (index === array.length - 1 && typeof range === 'string' && range.endsWith(')')) {
          range = range.slice(0, -1) + ']';
        }
        
        return {
          range: range,
          count: bin.instances || 0
        };
      });
    };

    // Transform RSRP histogram
    const rsrpBins = fixHistogramRanges(apiData.rsrpHistogram || []);

    // Transform RSRQ histogram
    const rsrqBins = fixHistogramRanges(apiData.rsrqHistogram || []);

    // Transform SINR histogram
    const sinrBins = fixHistogramRanges(apiData.sinrHistogram || []);

    const result = {
      rsrpHistogram: { bins: rsrpBins },
      rsrqHistogram: { bins: rsrqBins },
      sinrHistogram: { bins: sinrBins }
    };

    return result;
  }

  /**
   * Process anomalous signal data from API response (reads response only once)
   */
  async processAnomalousSignalData(response) {
    if (!response || response.status !== 200) {
      return {
        anomalousSignalValues: null
      };
    }

    try {
      // Read the response body only once
      const apiData = await response.json();

      return {
        // Store raw data for multi-series anomalous signal chart
        anomalousSignalValues: apiData
      };
    } catch (error) {
      console.error('❌ Error processing anomalous signal data:', error);
      return {
        anomalousSignalValues: null
      };
    }
  }


  /**
   * Transform Anomalous Signal Stats API response to stats format
   */
  transformAnomalousSignalStats(apiData) {
    if (!apiData) {
      return {
        rsrpStats: { total: 0, anomalous: 0, percentage: 0 },
        rsrqStats: { total: 0, anomalous: 0, percentage: 0 },
        sinrStats: { total: 0, anomalous: 0, percentage: 0 }
      };
    }


    const result = {
      rsrpStats: {
        total: apiData.rsrpStats?.totalInstances || 0,
        anomalous: apiData.rsrpStats?.anomalousInstances || 0,
        percentage: apiData.rsrpStats?.percentage || 0
      },
      rsrqStats: {
        total: apiData.rsrqStats?.totalInstances || 0,
        anomalous: apiData.rsrqStats?.anomalousInstances || 0,
        percentage: apiData.rsrqStats?.percentage || 0
      },
      sinrStats: {
        total: apiData.sinrStats?.totalInstances || 0,
        anomalous: apiData.sinrStats?.anomalousInstances || 0,
        percentage: apiData.sinrStats?.percentage || 0
      }
    };

    return result;
  }

  /**
   * Transform Data Stall Subsystem API response to pie chart format
   */
  transformDataStallSubsystem(subsystemDistribution) {
    if (!subsystemDistribution || !Array.isArray(subsystemDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors for data stall subsystems
    const colors = CHART_COLORS;
    
    return {
      labels: subsystemDistribution.map(item => item.dataStallSubSystem || 'Unknown'),
      data: subsystemDistribution.map(item => item.occurrence || 0),
      colors: subsystemDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Data Stall Recovery Actions API response to pie chart format
   */
  transformDataStallRecoveryActions(recoveryActionsDistribution) {
    if (!recoveryActionsDistribution || !Array.isArray(recoveryActionsDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors for data stall recovery actions
    const colors = CHART_COLORS;
    
    return {
      labels: recoveryActionsDistribution.map(item => item.dataStallRecoveryAction || 'Unknown'),
      data: recoveryActionsDistribution.map(item => item.occurrence || 0),
      colors: recoveryActionsDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Data Stall Timeline API response to event series format
   */
  transformDataStallTimeline(timeline) {
    if (!timeline || !Array.isArray(timeline)) {
      return { eventSeries: [] };
    }


    // Group timeline data by subsystem and extract recovery actions as a dedicated series.
    const timelineBySubsystem = {};
    const recoveryActionEvents = [];
    timeline.forEach(event => {
      const recoveryAction = event.dataStallRecoveryAction || event.recoveryAction || null;
      const recoveryStatus = event.dataStallRecoveryStatus || event.recoveryStatus || null;

      if (event.timestampEpoch && recoveryAction) {
        recoveryActionEvents.push({
          x: event.timestampEpoch,
          recoveryAction,
          recoveryStatus,
          subsystem: event.dataStallSubSystem || event.subsystem || 'Unknown'
        });
        return;
      }

      const subsystem = event.dataStallSubSystem || 'Unknown';
      if (!timelineBySubsystem[subsystem]) {
        timelineBySubsystem[subsystem] = [];
      }
      // Only add events that have real timestamps from API
      if (event.timestampEpoch) {
        timelineBySubsystem[subsystem].push({
          x: event.timestampEpoch
        });
      }
    });

    // Convert to event series format with colors
    const subsystemColors = {
      'WWAN': '#e74c3c',
      'RMNET': '#3498db',
      'IPA': '#f39c12',
      'MODEM': '#2ecc71',
      'CELLULAR': '#9b59b6',
      'NETWORK': '#1abc9c'
    };

    const eventSeries = Object.keys(timelineBySubsystem).map(subsystem => ({
      name: subsystem,
      events: timelineBySubsystem[subsystem].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: subsystemColors[subsystem] || '#95a5a6'
    }));

    if (recoveryActionEvents.length > 0) {
      eventSeries.push({
        name: 'RECOVERY ACTION',
        events: recoveryActionEvents.sort((a, b) => new Date(a.x) - new Date(b.x)),
        color: '#9b59b6'
      });
    }

    const result = { eventSeries };
    return result;
  }

  /**
   * Transform Subsystem Restart Events API response to pie chart format
   */
  transformSubsystemRestartEvents(restartEventDistribution) {
    if (!restartEventDistribution || !Array.isArray(restartEventDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    // Use vibrant colors for subsystem restart events
    const colors = CHART_COLORS;
    
    return {
      labels: restartEventDistribution.map(item => item.subsystemEvent || 'Unknown'),
      data: restartEventDistribution.map(item => item.occurred || 0),
      colors: restartEventDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Subsystem Restart Timeline API response to event series format
   */
  transformSubsystemRestartTimeline(timeline) {
    if (!timeline || !Array.isArray(timeline)) {
      return { eventSeries: [] };
    }


    // Group timeline data by subsystem event
    const timelineByEvent = {};
    timeline.forEach(event => {
      const subsystemEvent = event.subsystemEvent || 'Unknown';
      if (!timelineByEvent[subsystemEvent]) {
        timelineByEvent[subsystemEvent] = [];
      }
      // Only add events that have real timestamps from API
      if (event.timestampEpoch) {
        timelineByEvent[subsystemEvent].push({
          x: event.timestampEpoch
        });
      }
    });

    // Convert to event series format with colors
    const eventColors = {
      'MODEM_SUBSYSTEM_UP': '#2ecc71',
      'MODEM_SUBSYSTEM_DOWN': '#e74c3c',
      'WLAN_SUBSYSTEM_UP': '#3498db',
      'WLAN_SUBSYSTEM_DOWN': '#f39c12',
      'GPS_SUBSYSTEM_UP': '#9b59b6',
      'GPS_SUBSYSTEM_DOWN': '#1abc9c'
    };

    const eventSeries = Object.keys(timelineByEvent).map(subsystemEvent => ({
      name: subsystemEvent,
      events: timelineByEvent[subsystemEvent].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: eventColors[subsystemEvent] || '#95a5a6'
    }));

    const result = { eventSeries };
    return result;
  }

  /**
   * Map frontend time range values to backend range parameter values
   */
  mapTimeRangeToBackendRange(timeRange) {
    // Handle custom range objects - use legacy fromDate/toDate approach
    if (typeof timeRange === 'object' && timeRange.type === 'custom') {
      return null; // Will use fromDate/toDate parameters
    }
    
    // Map frontend values to backend values for cached ranges
    const rangeMapping = {
      '24h': '1d',  // Frontend shows "1d" but sends "24h", backend expects "1d"
      '1d': '1d',   // Direct mapping
      '7d': '7d',   // Direct mapping
      '14d': '14d', // Direct mapping
      '30d': '30d'  // Direct mapping
    };
    
    return rangeMapping[timeRange] || null;
  }

  /**
   * Build query params for network health aggregate endpoint.
   * For custom ranges, this intentionally rounds to UTC day boundaries.
   */
  buildNetworkHealthQueryParams(timeRange) {
    const backendRange = this.mapTimeRangeToBackendRange(timeRange);
    if (backendRange) {
      return `range=${backendRange}`;
    }

    if (typeof timeRange === 'object' && timeRange.type === 'custom') {
      const from = new Date(timeRange.from);
      const to = new Date(timeRange.to);

      const fromUtc = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), 0, 0, 0, 0);
      const toUtcExclusive = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() + 1, 0, 0, 0, 0);

      const fromDate = Math.floor(fromUtc / 1000);
      const toDate = Math.floor(toUtcExclusive / 1000);

      return `fromDate=${fromDate}&toDate=${toDate}`;
    }

    const { fromDate, toDate } = this.timeRangeToEpoch(timeRange);
    return `fromDate=${fromDate}&toDate=${toDate}`;
  }

  /**
   * Convert UI time range to integer durationDays for aggregate device-info endpoint.
   */
  timeRangeToDurationDays(timeRange) {
    if (typeof timeRange === 'string') {
      const mapping = {
        '5m': 1,
        '15m': 1,
        '30m': 1,
        '1h': 1,
        '3h': 1,
        '6h': 1,
        '12h': 1,
        '24h': 1,
        '2d': 2,
        '7d': 7,
        '14d': 14,
        '30d': 30,
        '90d': 90
      };
      return mapping[timeRange] || 7;
    }

    if (typeof timeRange === 'object' && timeRange.type === 'custom') {
      const from = new Date(timeRange.from);
      const to = new Date(timeRange.to);
      const fromUtc = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), 0, 0, 0, 0);
      const toUtcExclusive = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() + 1, 0, 0, 0, 0);
      const diffDays = Math.max(1, Math.ceil((toUtcExclusive - fromUtc) / (24 * 60 * 60 * 1000)));
      return Math.min(365, diffDays);
    }

    return 7;
  }

  /**
   * Build UTC date-window query params for device-health aggregate endpoint.
   */
  buildDeviceHealthDateWindow(timeRange) {
    if (typeof timeRange === 'object' && timeRange.type === 'custom') {
      const from = new Date(timeRange.from);
      const to = new Date(timeRange.to);
      const startDate = this.formatDateUtc(from);
      const endDate = this.formatDateUtc(to);
      return { startDate, endDate };
    }

    const durationDays = this.timeRangeToDurationDays(timeRange);
    const now = new Date();
    const endUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const startUtc = new Date(endUtc.getTime() - ((durationDays - 1) * 24 * 60 * 60 * 1000));

    return {
      startDate: this.formatDateUtc(startUtc),
      endDate: this.formatDateUtc(endUtc)
    };
  }

  formatDateUtc(date) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  bucketToIso(bucketValue) {
    if (bucketValue == null) return null;

    if (typeof bucketValue === 'number') {
      const ms = bucketValue > 9999999999 ? bucketValue : bucketValue * 1000;
      return new Date(ms).toISOString();
    }

    const numeric = Number(bucketValue);
    if (!Number.isNaN(numeric) && bucketValue !== '') {
      const ms = numeric > 9999999999 ? numeric : numeric * 1000;
      return new Date(ms).toISOString();
    }

    const dt = new Date(bucketValue);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  }

  groupAggregateSeries(rows, keyField, valueField, prefix = '') {
    const grouped = {};
    rows.forEach((row) => {
      const key = row[keyField] || 'Unknown';
      const timestamp = this.bucketToIso(row.bucket_start_epoch_sec || row.timestamp_epoch_sec);
      if (!timestamp) return;

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({
        x: timestamp,
        y: Number(row[valueField] ?? 0)
      });
    });

    return Object.keys(grouped).map((key, index) => ({
      name: `${prefix}${key}`,
      data: grouped[key].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: CHART_COLORS[index % CHART_COLORS.length],
      hidden: index > 0
    }));
  }

  transformDeviceHealthThermal(rows) {
    return this.groupAggregateSeries(rows, 'sensor_type', 'max_metric_value_celsius');
  }

  transformDeviceHealthFlash(rows) {
    return this.groupAggregateSeries(rows, 'partition_name', 'used_memory_mb');
  }

  transformDeviceHealthMemory(rows) {
    return this.groupAggregateSeries(rows, 'sub_system', 'used_memory_mb_max');
  }

  transformDeviceHealthCpu(rows) {
    return this.groupAggregateSeries(rows, 'cpu_id', 'usr_percent', 'CPU ');
  }

  transformDeviceHealthSubsystemRestartEvents(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { labels: [], data: [], colors: [] };
    }

    const counts = {};
    rows.forEach((row) => {
      const label = `${row.sub_system || 'Unknown'}:${row.subsystem_restart_event || 'UNKNOWN'}`;
      counts[label] = (counts[label] || 0) + 1;
    });

    const labels = Object.keys(counts);
    return {
      labels,
      data: labels.map((label) => counts[label]),
      colors: labels.map((_, index) => CHART_COLORS[index % CHART_COLORS.length])
    };
  }

  transformDeviceHealthSubsystemRestartTimeline(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { eventSeries: [] };
    }

    const grouped = {};
    rows.forEach((row) => {
      const name = `${row.sub_system || 'Unknown'}:${row.subsystem_restart_event || 'UNKNOWN'}`;
      const timestamp = this.bucketToIso(row.timestamp_epoch_sec);
      if (!timestamp) return;
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push({ x: timestamp });
    });

    const eventSeries = Object.keys(grouped).map((name, index) => ({
      name,
      events: grouped[name].sort((a, b) => new Date(a.x) - new Date(b.x)),
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));

    return { eventSeries };
  }

  /**
   * Get aggregate dashboard data from WBC API.
   * Uses the retained gateway range contract; custom ranges are not supported
   * by this cached aggregate projection.
   */
  async getAggregateDashboard(timeRange = '24h') {

    // Validate supported ranges
    const SUPPORTED_RANGES = ['1d', '7d', '14d', '30d'];
    const backendRange = this.mapTimeRangeToBackendRange(timeRange);
    
    if (backendRange && !SUPPORTED_RANGES.includes(backendRange)) {
      return {
        cacheError: true,
        message: `Unsupported time range: ${timeRange}. Supported ranges: ${SUPPORTED_RANGES.join(', ')}`,
        timeRange: timeRange
      };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      let aggregateUrl;
      
      if (backendRange) {
        // Use new cached range parameter for fast response
        aggregateUrl = `${this.wbcApiBaseUrl}/aggregates/dashboard?range=${backendRange}`;
      } else {
        return {
          cacheError: true,
          message: 'Custom dashboard ranges are not supported. Choose 1d, 7d, 14d, or 30d.',
          timeRange
        };
      }
      
      
      const response = await fetch(aggregateUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle cache miss (400 Bad Request) gracefully
      if (response.status === 400) {
        return {
          cacheError: true,
          message: 'Cache error - data not available for this time range',
          timeRange: timeRange,
          details: 'The requested data is not available in the cache. Please try a different time range or check back later.'
        };
      }

      if (response.status === 204) {
        return {
          cacheError: true,
          message: 'No data available for this time range',
          timeRange: timeRange
        };
      }

      if (!response.ok) {
        return {
          cacheError: true,
          message: 'Cache error - data not available',
          timeRange: timeRange,
          details: `Server returned ${response.status}: ${response.statusText}`
        };
      }

      const aggregateData = await response.json();
      
      // Handle null/empty response
      if (!aggregateData) {
        return {
          cacheError: true,
          message: 'Cache error - no data returned from server',
          timeRange: timeRange
        };
      }


      const result = this.transformAggregateData(aggregateData);
      return result;

    } catch (error) {
      console.error('❌ Aggregate Dashboard API Error:', error);
      
      if (error.name === 'AbortError') {
        return {
          cacheError: true,
          message: 'Request timeout - cache error',
          timeRange: timeRange,
          details: 'The request timed out. Please try again or select a different time range.'
        };
      }
      
      if (error.message.includes('fetch')) {
        return {
          cacheError: true,
          message: 'Network error - cache not accessible',
          timeRange: timeRange,
          details: 'Unable to connect to the server. Please check your connection and try again.'
        };
      }

      // Return cache error for any other errors
      return {
        cacheError: true,
        message: 'Cache error - data not available',
        timeRange: timeRange,
        details: error.message || 'An unexpected error occurred while loading aggregate data.'
      };
    }
  }

  /**
   * Fetch the list of device tags that have computed aggregate data.
   * @param {string|null} date - optional ISO date string (yyyy-MM-dd), defaults to yesterday on server
   * @returns {Promise<{date: string, tags: string[], count: number}>}
   */
  async getAggregateTags(date = null) {
    const url = `${this.wbcApiBaseUrl}/aggregates/tags${date ? `?date=${date}` : ''}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        console.warn('getAggregateTags: server returned', response.status);
        return { date: null, tags: [], count: 0 };
      }
      return await response.json();
    } catch (error) {
      console.error('getAggregateTags error:', error);
      return { date: null, tags: [], count: 0 };
    }
  }

  /**
   * Get raw CPU Health statistics for Aggregate or Device Tag analytics.
   * The gateway calculates statistics over every selected protobuf CPU metric.
   */
  async getCpuHealth(timeRange = '1d', deviceTag = null, cpuId = 'ALL', metric = 'ALL') {
    const { fromDate, toDate } = this.timeRangeToEpoch(timeRange);
    const params = new URLSearchParams({
      fromDate: String(fromDate),
      toDate: String(toDate)
    });
    const appendValues = (name, values) => {
      const selectedValues = Array.isArray(values) ? values : [values];
      const normalizedValues = selectedValues.filter(value => value !== null && value !== undefined && value !== '');
      (normalizedValues.length ? normalizedValues : ['ALL']).forEach(value => params.append(name, value));
    };
    appendValues('cpuId', cpuId);
    appendValues('metric', metric);
    if (deviceTag) params.set('deviceTag', deviceTag);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(`${this.wbcApiBaseUrl}/cpu-health?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {
          // Keep the HTTP status message when the gateway does not return JSON.
        }
        throw new Error(message);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get aggregate Memory Health statistics for the selected subsystems.
   */
  async getMemoryHealth(timeRange = '1d', deviceTag = null, subSystem = 'ALL') {
    const { fromDate, toDate } = this.timeRangeToEpoch(timeRange);
    const params = new URLSearchParams({
      fromDate: String(fromDate),
      toDate: String(toDate)
    });
    const selectedValues = Array.isArray(subSystem) ? subSystem : [subSystem];
    const normalizedValues = selectedValues.filter(value => value !== null && value !== undefined && value !== '');
    (normalizedValues.length ? normalizedValues : ['ALL']).forEach(value => params.append('subSystem', value));
    if (deviceTag) params.set('deviceTag', deviceTag);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(`${this.wbcApiBaseUrl}/memory-health?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {
          // Keep the HTTP status message when the gateway does not return JSON.
        }
        throw new Error(message);
      }
      return await response.json();
    } catch (error) {
      if (error?.name === 'AbortError') {
        const timeoutError = new Error('The Memory Health request timed out.');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get per-device thermal stats grouped by sensor type for Aggregate or Device Tag analytics.
   * Thermal is day-based (N complete days ending yesterday), not an epoch window.
   * @param {string} timeRange  - one of '1d', '7d', '30d'
   * @param {string|null} deviceTag - device tag value, or null for aggregate view
   * @param {string} sensorType - 'ALL' or a single sensor type
   */
  async getThermalDeviceStats(timeRange = '7d', deviceTag = null, sensorTypes = 'ALL') {
    const days = { '1d': 1, '7d': 7, '30d': 30 }[timeRange] || 7;
    const path = deviceTag
      ? `/thermal/device-stats/tag/${encodeURIComponent(deviceTag)}`
      : '/thermal/device-stats/aggregate';
    const params = new URLSearchParams({ days: String(days) });
    const appendValues = (name, values) => {
      const selectedValues = Array.isArray(values) ? values : [values];
      const normalizedValues = selectedValues.filter(value => value !== null && value !== undefined && value !== '');
      (normalizedValues.length ? normalizedValues : ['ALL']).forEach(value => params.append(name, value));
    };
    appendValues('sensorTypes', sensorTypes);
    params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
    const url = `${this.wbcApiBaseUrl}${path}?${params.toString()}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {
          // Keep the HTTP status message when the gateway does not return JSON.
        }
        throw new Error(message);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get the distinct thermal sensor types available for the given scope.
   * Uses a fixed 90-day lookback on the backend — independent of D/W/M duration.
   * Returns { sensorTypes: [...] }.
   */
  async getThermalSensorTypes(deviceTag = null) {
    const path = deviceTag
      ? `/thermal/sensor-types/tag/${encodeURIComponent(deviceTag)}`
      : '/thermal/sensor-types/aggregate';
    const url = `${this.wbcApiBaseUrl}${path}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {
          // Keep the HTTP status message when the gateway does not return JSON.
        }
        throw new Error(message);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get distinct flash partition names for dropdown population.
   * Uses a fixed 90-day server-side lookback — not tied to the selected duration chip.
   * @param {string|null} deviceTag - device tag value, or null for aggregate view
   */
  async getFlashPartitions(deviceTag = null) {
    const params = new URLSearchParams();
    if (deviceTag) params.set('deviceTag', deviceTag);
    const url = `${this.wbcApiBaseUrl}/flash-health/partitions${params.toString() ? '?' + params.toString() : ''}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get per-device flash/storage stats grouped by partition for Aggregate or Device Tag analytics.
   * Uses complete UTC days ending yesterday (day boundary = midnight UTC).
   * @param {string} timeRange      - one of '1d', '7d', '30d'
   * @param {string|null} deviceTag - device tag value, or null for aggregate view
   * @param {string|Array} partitionNames - 'ALL' or array of partition name strings
   */
  async getFlashHealth(timeRange = '7d', deviceTag = null, partitionNames = 'ALL') {
    const days = { '1d': 1, '7d': 7, '30d': 30 }[timeRange] || 7;
    const params = new URLSearchParams({ days: String(days) });
    const appendValues = (name, values) => {
      const selectedValues = Array.isArray(values) ? values : [values];
      const normalizedValues = selectedValues.filter(v => v !== null && v !== undefined && v !== '');
      (normalizedValues.length ? normalizedValues : ['ALL']).forEach(v => params.append(name, v));
    };
    appendValues('partitionName', partitionNames);
    if (deviceTag) params.set('deviceTag', deviceTag);
    params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
    try {
      const response = await fetch(`${this.wbcApiBaseUrl}/flash-health?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get dashboard aggregate data for a specific device tag.
   * Response shape is identical to getAggregateDashboard() — same chart components work.
   * @param {string} tag       - device tag value
   * @param {string} timeRange - one of '1d', '7d', '14d', '30d'
   */
  async getDeviceTagDashboard(tag, timeRange = '7d') {
    const SUPPORTED_RANGES = ['1d', '7d', '14d', '30d'];
    const backendRange = this.mapTimeRangeToBackendRange(timeRange);

    if (!backendRange || !SUPPORTED_RANGES.includes(backendRange)) {
      return {
        cacheError: true,
        message: `Unsupported time range: ${timeRange}. Supported ranges: ${SUPPORTED_RANGES.join(', ')}`,
        timeRange
      };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      const url = `${this.wbcApiBaseUrl}/aggregates/dashboard/by-tag?range=${backendRange}&tag=${encodeURIComponent(tag)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 400) {
        return {
          cacheError: true,
          message: `No aggregate data available for tag: ${tag}`,
          timeRange,
          tag
        };
      }

      if (!response.ok) {
        return {
          cacheError: true,
          message: 'Cache error - data not available',
          timeRange,
          tag,
          details: `Server returned ${response.status}: ${response.statusText}`
        };
      }

      const aggregateData = await response.json();
      if (!aggregateData) {
        return { cacheError: true, message: 'No data returned from server', timeRange, tag };
      }

      return this.transformAggregateData(aggregateData);
    } catch (error) {
      console.error('getDeviceTagDashboard error:', error);
      return {
        cacheError: true,
        message: error.name === 'AbortError' ? 'Request timeout - cache error' : 'Cache error - data not available',
        timeRange,
        tag,
        details: error.message
      };
    }
  }

  /**
   * Get aggregate throughput statistics from the condensed peak-rate table.
   * The same endpoint supports fleet and device-tag views.
   */
  async getThroughputStats(timeRange = '7d', tag = null, thresholdMbps = null) {
    const daysByRange = { '1d': 1, '24h': 1, '7d': 7, '14d': 14, '30d': 30 };
    const days = daysByRange[timeRange] || 7;
    const path = tag
      ? `/throughput/tag/${encodeURIComponent(tag)}`
      : '/throughput/aggregate';
    const params = new URLSearchParams({ days: String(days) });
    if (Number.isFinite(Number(thresholdMbps)) && Number(thresholdMbps) > 0) {
      params.set('thresholdMbps', String(thresholdMbps));
    }
    const url = `${this.wbcApiBaseUrl}${path}?${params.toString()}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });

      if (response.status === 204) return transformThroughputStats(null);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return transformThroughputStats(await response.json());
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Throughput request timed out - please try again');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get fleet health top-offenders per KPI domain.
   * Queries the backend directly against the daily aggregate tables — no separate pre-aggregation step.
   */
  async getFleetHealth(timeRange = '7d', tag = null) {
    const SUPPORTED_RANGES = ['1d', '7d', '14d', '30d'];
    const backendRange = this.mapTimeRangeToBackendRange(timeRange);

    if (!backendRange || !SUPPORTED_RANGES.includes(backendRange)) {
      return {
        cacheError: true,
        message: `Unsupported time range: ${timeRange}. Supported ranges: ${SUPPORTED_RANGES.join(', ')}`,
        timeRange
      };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);

      let url = `${this.wbcApiBaseUrl}/aggregates/fleet-health?range=${backendRange}`;
      if (tag && tag !== 'all') url += `&tag=${encodeURIComponent(tag)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 400) {
        return {
          cacheError: true,
          message: 'Fleet health data not available for this time range',
          timeRange
        };
      }

      if (!response.ok) {
        return {
          cacheError: true,
          message: 'Error fetching fleet health data',
          timeRange,
          details: `Server returned ${response.status}: ${response.statusText}`
        };
      }

      return await response.json();
    } catch (error) {
      console.error('getFleetHealth error:', error);
      return {
        cacheError: true,
        message: error.name === 'AbortError' ? 'Request timeout' : 'Error fetching fleet health data',
        timeRange,
        details: error.message
      };
    }
  }

  async getFleetSubdomainTrend(timeRange = '7d') {
    const days = this.timeRangeToDurationDays(timeRange);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
      const response = await fetch(`${this.wbcApiBaseUrl}/health/fleet/subdomain-trend?days=${days}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.status === 400) {
        return { cacheError: true, message: 'Subdomain trend data not available for this time range', timeRange };
      }
      if (!response.ok) {
        return { cacheError: true, message: 'Error fetching subdomain trend data', timeRange, details: `Server returned ${response.status}: ${response.statusText}` };
      }
      return await response.json();
    } catch (error) {
      return {
        cacheError: true,
        message: error.name === 'AbortError' ? 'Request timeout' : 'Error fetching subdomain trend data',
        timeRange,
        details: error.message
      };
    }
  }

  async getFleetDeviceClassification(timeRange = '7d') {
    const days = this.timeRangeToDurationDays(timeRange);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
      const response = await fetch(`${this.wbcApiBaseUrl}/health/fleet/device-classification?days=${days}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.status === 400) {
        return { cacheError: true, message: 'Device classification data not available for this time range', timeRange };
      }
      if (!response.ok) {
        return { cacheError: true, message: 'Error fetching device classification data', timeRange, details: `Server returned ${response.status}: ${response.statusText}` };
      }
      return await response.json();
    } catch (error) {
      return {
        cacheError: true,
        message: error.name === 'AbortError' ? 'Request timeout' : 'Error fetching device classification data',
        timeRange,
        details: error.message
      };
    }
  }

  async getFleetSubdomainMetadata() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.panelApiTimeout);
      const response = await fetch(`${this.wbcApiBaseUrl}/health/fleet/subdomain-metadata`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        return { cacheError: true, message: 'Error fetching subdomain metadata', details: `Server returned ${response.status}: ${response.statusText}` };
      }
      return await response.json();
    } catch (error) {
      return {
        cacheError: true,
        message: error.name === 'AbortError' ? 'Request timeout' : 'Error fetching subdomain metadata',
        details: error.message
      };
    }
  }

  /**
   * Transform Aggregate Dashboard API response to component format
   */
  transformAggregateData(apiData) {
    if (!apiData) {
      return this.getEmptyAggregateData();
    }


    const result = {
      // Overview section
      overview: {
        totalDevices: apiData.deviceStats?.totalDevices || 0
      },

      // Network Health section
      networkHealth: {
        // Pie charts
        registrationStatus: this.transformAggregateRegistrationStatus(apiData),
        registrationFailures: this.transformAggregateRegistrationFailures(apiData.networkEvents?.registrationFailures),
        pdnStatus: this.transformAggregatePdnStatus(apiData.networkEvents?.pdnStatus),
        pdnFailureReasons: this.transformAggregatePdnFailureReasons(apiData.networkEvents?.pdnFailureReasons),
        pdnApnTypeDistribution: this.transformAggregatePdnApnTypeDistribution(apiData.networkEvents?.pdnApnTypeDistribution),
        
        // Signal strength histograms
        rsrpHistogram: this.transformAggregateHistogram(apiData.signalStrength?.rsrpHistogram),
        rsrqHistogram: this.transformAggregateHistogram(apiData.signalStrength?.rsrqHistogram),
        sinrHistogram: this.transformAggregateHistogram(apiData.signalStrength?.sinrHistogram),

        // Common-header identifier distributions. CGI is intentionally kept as a string end-to-end.
        identifierDistributions: this.transformIdentifierDistributions(apiData.networkIdentifiers)
      },

      // Boot KPI section
      bootKpi: {
        kpiDistribution: this.transformAggregateKpiDistribution(apiData.bootKpis?.typeDistribution),
        timingHistograms: this.transformAggregateTimingHistograms(apiData.bootKpis?.timingHistograms)
      }
    };

    return result;
  }

  /**
   * Get empty aggregate data structure
   */
  getEmptyAggregateData() {
    return {
      overview: {
        totalDevices: 0
      },
      networkHealth: {
        registrationStatus: { labels: [], data: [], colors: [] },
        registrationFailures: { labels: [], data: [], colors: [] },
        pdnStatus: { labels: [], data: [], colors: [] },
        pdnFailureReasons: { labels: [], data: [], colors: [] },
        pdnApnTypeDistribution: { labels: [], data: [], colors: [] },
        rsrpHistogram: { bins: [] },
        rsrqHistogram: { bins: [] },
        sinrHistogram: { bins: [] },
        identifierDistributions: {
          pci: { bins: [] },
          earfcn: { bins: [] },
          arfcn: { datasets: [] },
          cgi: { bins: [] },
          mncMcc: { bins: [] }
        }
      },
      bootKpi: {
        kpiDistribution: { labels: [], data: [], colors: [] },
        timingHistograms: {}
      }
    };
  }

  /**
   * Transform Aggregate Registration Status to pie chart format
   * Uses total device count since all devices in the system are considered registered
   */
  transformAggregateRegistrationStatus(apiData) {
    const statusDistribution = apiData?.networkEvents?.registrationStatus;
    if (!statusDistribution || !Array.isArray(statusDistribution) || statusDistribution.length === 0) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = [CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[0]];

    return {
      labels: statusDistribution.map(item => item.registrationStatus || 'Unknown'),
      data: statusDistribution.map(item => item.count || 0),
      colors: statusDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate Registration Failures to pie chart format
   */
  transformAggregateRegistrationFailures(registrationFailures) {
    if (!registrationFailures || !Array.isArray(registrationFailures)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#e74c3c', '#f39c12', '#e67e22', '#c0392b'];
    
    return {
      labels: registrationFailures.map(item => item.rejectCause || 'Unknown'),
      data: registrationFailures.map(item => item.count || 0),
      colors: registrationFailures.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate PDN Status to pie chart format
   */
  transformAggregatePdnStatus(pdnStatus) {
    if (!pdnStatus || !Array.isArray(pdnStatus)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#3498db', '#2ecc71', '#e74c3c', '#9b59b6', '#f39c12', '#1abc9c'];
    
    return {
      labels: pdnStatus.map(item => item.pdnStatus || 'Unknown'),
      data: pdnStatus.map(item => item.count || 0),
      colors: pdnStatus.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate PDN Failure Reasons to pie chart format
   */
  transformAggregatePdnFailureReasons(pdnFailureReasons) {
    if (!pdnFailureReasons || !Array.isArray(pdnFailureReasons)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c'];

    return {
      labels: pdnFailureReasons.map(item => item.failureReason || 'Unknown'),
      data: pdnFailureReasons.map(item => item.count || 0),
      colors: pdnFailureReasons.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate PDN APN Type Distribution to pie chart format
   */
  transformAggregatePdnApnTypeDistribution(pdnApnTypeDistribution) {
    if (!pdnApnTypeDistribution || !Array.isArray(pdnApnTypeDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c'];

    return {
      labels: pdnApnTypeDistribution.map(item => item.apnType || 'Unknown'),
      data: pdnApnTypeDistribution.map(item => item.count || 0),
      colors: pdnApnTypeDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate Histogram to histogram format
   */
  transformAggregateHistogram(histogramData) {
    if (!histogramData || !Array.isArray(histogramData)) {
      return { bins: [] };
    }

    return {
      bins: histogramData.map(bin => ({
        range: bin.binInterval || 'Unknown',
        count: bin.instances || 0
      }))
    };
  }

  transformIdentifierDistributions(identifierData) {
    const bins = (values, valueField = 'value') => ({
      bins: Array.isArray(values) ? values.map(item => ({
        range: String(item?.[valueField] ?? ''),
        count: Number(item?.count) || 0
      })) : []
    });
    const arfcnValues = Array.isArray(identifierData?.arfcn) ? identifierData.arfcn : [];
    const arfcnLabels = ['LTE', 'NR', 'OTHER/UNKNOWN'];

    return {
      pci: bins(identifierData?.pci),
      earfcn: bins(identifierData?.earfcn),
      arfcn: {
        datasets: arfcnLabels.map((rat, index) => ({
          label: rat === 'OTHER/UNKNOWN' ? 'Other/Unknown' : rat,
          color: ['#3498db', '#2ecc71', '#95a5a6'][index],
          bins: arfcnValues
            .filter(item => item?.rat === rat)
            .map(item => ({ range: String(item?.value ?? ''), count: Number(item?.count) || 0 }))
        }))
      },
      cgi: bins(identifierData?.cgi),
      mncMcc: bins(identifierData?.mncMcc, 'label')
    };
  }

  /**
   * Transform Aggregate KPI Distribution to pie chart format
   */
  transformAggregateKpiDistribution(kpiDistribution) {
    if (!kpiDistribution || !Array.isArray(kpiDistribution)) {
      return { labels: [], data: [], colors: [] };
    }

    const colors = ['#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#9b59b6', '#1abc9c', '#e67e22', '#95a5a6', '#34495e'];
    
    return {
      labels: kpiDistribution.map(item => item.kpiType || 'Unknown'),
      data: kpiDistribution.map(item => item.count || 0),
      colors: kpiDistribution.map((_, index) => colors[index % colors.length])
    };
  }

  /**
   * Transform Aggregate Timing Histograms to statistical summaries for HorizontalStatsCard
   */
  transformAggregateTimingHistograms(timingHistograms) {
    if (!timingHistograms || typeof timingHistograms !== 'object') {
      return {};
    }

    const transformedStats = {};
    
    Object.entries(timingHistograms).forEach(([kpiType, timings]) => {
      if (Array.isArray(timings) && timings.length > 0) {
        // Convert timing array to statistical summary for HorizontalStatsCard
        transformedStats[kpiType] = this.calculateTimingStatistics(timings);
      } else if (timings && typeof timings === 'object' && !Array.isArray(timings)) {
        // Already aggregated stats format from new aggregate endpoint
        transformedStats[kpiType] = {
          count: Number(timings.count) || 0,
          mean: Number(timings.mean) || 0,
          min: Number(timings.min) || 0,
          max: Number(timings.max) || 0,
          stdDev: Number(timings.stdDev) || 0
        };
      } else {
        transformedStats[kpiType] = {
          count: 0,
          mean: 0,
          min: 0,
          max: 0,
          stdDev: 0
        };
      }
    });

    return transformedStats;
  }

  /**
   * Create histogram bins from timing data array
   */
  createHistogramBins(timings, binCount = 10) {
    if (!Array.isArray(timings) || timings.length === 0) {
      return { bins: [] };
    }

    const min = Math.min(...timings);
    const max = Math.max(...timings);
    
    // Handle case where all values are the same
    if (min === max) {
      return {
        bins: [{
          range: `${min.toFixed(2)}`,
          count: timings.length
        }]
      };
    }

    const binWidth = (max - min) / binCount;
    const bins = [];
    
    for (let i = 0; i < binCount; i++) {
      const binStart = min + (i * binWidth);
      const binEnd = min + ((i + 1) * binWidth);
      
      // For the last bin, include the maximum value
      const count = i === binCount - 1 
        ? timings.filter(t => t >= binStart && t <= binEnd).length
        : timings.filter(t => t >= binStart && t < binEnd).length;
      
      bins.push({
        range: `${binStart.toFixed(2)}-${binEnd.toFixed(2)}`,
        count: count
      });
    }
    
    return { bins };
  }

  /**
   * Preload all data for a device
   */
  async preloadDeviceData(deviceId, timeRange = '24h') {
    if (!deviceId) return null;

    try {
      // Keep this compatibility method on the current aggregate readers.
      const [deviceInfo, networkHealth, deviceHealth, bootKpi] = await Promise.all([
        this.getDeviceInfoAggregate(deviceId, timeRange),
        this.getNetworkHealthAggregate(deviceId, timeRange),
        this.getDeviceHealthAggregate(deviceId, timeRange),
        this.getBootKpiAggregate(deviceId, timeRange)
      ]);

      return {
        metadata: deviceInfo.metadata || null,
        networkHealth,
        deviceHealth,
        deviceInfo,
        bootKpi,
        loadedAt: new Date().toISOString(),
        timeRange
      };
    } catch (error) {
      console.error('Error preloading device data:', error);
      throw error;
    }
  }

  /**
   * Generate a hash for data comparison (Grafana-style smart refresh)
   */
  generateDataHash(data) {
    try {
      // Create a stable string representation of the data
      const dataString = JSON.stringify(data, Object.keys(data).sort());
      // Simple hash function (for demo - in production use crypto.subtle.digest)
      let hash = 0;
      for (let i = 0; i < dataString.length; i++) {
        const char = dataString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return hash.toString(36);
    } catch (error) {
      console.warn('Error generating data hash:', error);
      return Date.now().toString(36); // Fallback to timestamp
    }
  }

  /**
   * Compare two data objects to check if they're different
   */
  hasDataChanged(newData, oldData) {
    if (!oldData) return true; // No previous data, so it's "changed"
    if (!newData) return false; // No new data, so no change
    
    try {
      const newHash = this.generateDataHash(newData);
      const oldHash = this.generateDataHash(oldData);
      return newHash !== oldHash;
    } catch (error) {
      console.warn('Error comparing data:', error);
      return true; // Assume changed if comparison fails
    }
  }

  /**
   * Smart refresh: fetch new data and compare with existing
   */
  async smartRefresh(deviceId, timeRange, existingData) {
    if (!deviceId) return { hasChanges: false, data: null };

    try {
      // Fetch fresh data
      const newData = await this.preloadDeviceData(deviceId, timeRange);
      
      // Compare with existing data
      const hasChanges = this.hasDataChanged(newData, existingData);
      
      return {
        hasChanges,
        data: newData,
        lastUpdated: new Date().toISOString(),
        message: hasChanges ? 'Data updated' : 'No new data available'
      };
    } catch (error) {
      console.error('Error during smart refresh:', error);
      throw error;
    }
  }

  /**
   * Transform Data Stall Recovery Actions Timeline API response to event series format
   */
  transformDataStallRecoveryActionsTimeline(timeline) {
    if (!timeline || !Array.isArray(timeline)) {
      return { eventSeries: [] };
    }


    // Group all recovery actions into a single series
    const recoveryActionEvents = [];
    timeline.forEach(event => {
      // Only add events that have real timestamps from API
      if (event.timestamp) {
        recoveryActionEvents.push({
          x: event.timestamp,
          recoveryAction: event.recoveryAction,
          recoveryStatus: event.recoveryStatus,
          subsystem: event.subsystem
        });
      }
    });

    // Sort events by timestamp
    recoveryActionEvents.sort((a, b) => new Date(a.x) - new Date(b.x));

    // Create a single event series for all recovery actions
    const eventSeries = recoveryActionEvents.length > 0 ? [{
      name: 'RECOVERY ACTION',
      events: recoveryActionEvents,
      color: '#9b59b6' // Purple color for recovery actions
    }] : [];

    const result = { eventSeries };
    return result;
  }

  /**
   * Merge data stall timeline with recovery actions timeline
   */
  mergeDataStallTimelines(dataStallTimeline, recoveryActionsTimeline) {
    if (!dataStallTimeline || !recoveryActionsTimeline) {
      return dataStallTimeline || recoveryActionsTimeline || { eventSeries: [] };
    }


    // Combine event series from both timelines
    const mergedEventSeries = [
      ...(dataStallTimeline.eventSeries || []),
      ...(recoveryActionsTimeline.eventSeries || [])
    ];

    const result = { eventSeries: mergedEventSeries };
    return result;
  }
}

// Export singleton instance
export const deviceDataService = new DeviceDataService();
export default deviceDataService;
